/**
 * The zoom bar sits in the document column, which is wider than the paper:
 * the margin-number rail, the scrollbar gutter, and the page's side gap all
 * live in that column. Size the bar to the visible document instead.
 */
export interface PreviewChromeBox {
  left: number;
  width: number;
}

export function previewChromeBox(input: {
  /** Document column, including the margin rail. */
  hostLeft: number;
  hostWidth: number;
  /** Visible scrollport. Width excludes the scrollbar. */
  paneLeft: number;
  paneWidth: number;
  /** Rendered document: PDF page, white sheet, or image. */
  contentLeft: number;
  contentWidth: number;
}): PreviewChromeBox | null {
  const { hostLeft, hostWidth, paneLeft, paneWidth, contentLeft, contentWidth } = input;
  if (!(hostWidth > 0) || !(paneWidth > 0) || !(contentWidth > 0)) return null;

  const visibleLeft = Math.max(contentLeft, paneLeft);
  const visibleRight = Math.min(contentLeft + contentWidth, paneLeft + paneWidth);
  const width = visibleRight - visibleLeft;
  if (!(width > 0)) return null;

  const left = Math.max(0, visibleLeft - hostLeft);
  const maxWidth = Math.max(0, hostWidth - left);
  if (!(maxWidth > 0)) return null;
  return { left, width: Math.min(width, maxWidth) };
}

/** Painted width of an image or video that letterboxes inside its box. */
export function containedMediaSpan(
  box: { left: number; width: number; height: number },
  natural: { width: number; height: number },
): { left: number; width: number } {
  if (!(natural.width > 0) || !(natural.height > 0) || !(box.width > 0) || !(box.height > 0)) {
    return { left: box.left, width: box.width };
  }
  const scale = Math.min(box.width / natural.width, box.height / natural.height);
  const width = natural.width * scale;
  return { left: box.left + (box.width - width) / 2, width };
}

function laidOut(element: HTMLElement): boolean {
  const box = element.getBoundingClientRect();
  return box.width > 0 || box.height > 0;
}

function mediaSpan(element: HTMLElement): { left: number; width: number } {
  const box = element.getBoundingClientRect();
  const fit = getComputedStyle(element).objectFit;
  if (fit !== 'contain' && fit !== 'scale-down') return { left: box.left, width: box.width };
  const natural =
    element instanceof HTMLImageElement
      ? { width: element.naturalWidth, height: element.naturalHeight }
      : element instanceof HTMLVideoElement
        ? { width: element.videoWidth, height: element.videoHeight }
        : { width: 0, height: 0 };
  return containedMediaSpan(box, natural);
}

/** Horizontal span of the document inside the preview scroller. */
export function previewDocumentSpan(scroll: HTMLElement): { left: number; width: number } | null {
  const pages = [...scroll.querySelectorAll<HTMLElement>('.fp-page')].filter(laidOut);
  if (pages.length > 0) {
    let left = Infinity;
    let right = -Infinity;
    for (const page of pages) {
      const box = page.getBoundingClientRect();
      left = Math.min(left, box.left);
      right = Math.max(right, box.right);
    }
    return { left, width: right - left };
  }

  const sheet = [
    ...scroll.querySelectorAll<HTMLElement>('.fp-skel, .fp-text-view, .fp-docx, .fp-sheet'),
  ].find(laidOut);
  if (sheet) {
    const box = sheet.getBoundingClientRect();
    return { left: box.left, width: box.width };
  }

  const media = [...scroll.querySelectorAll<HTMLElement>('.fp-media img, .fp-media video')].find(
    laidOut,
  );
  if (media) return mediaSpan(media);
  return null;
}

/** Write `--fp-chrome-left` and `--fp-chrome-width` so the zoom bar matches the document. */
export function syncPreviewChrome(doc: HTMLElement, scroll: HTMLElement): void {
  const span = previewDocumentSpan(scroll);
  if (!span) return;
  const host = doc.getBoundingClientRect();
  const pane = scroll.getBoundingClientRect();
  const chrome = previewChromeBox({
    hostLeft: host.left,
    hostWidth: host.width,
    paneLeft: pane.left,
    paneWidth: scroll.clientWidth,
    contentLeft: span.left,
    contentWidth: span.width,
  });
  if (!chrome) return;
  const left = `${Math.round(chrome.left)}px`;
  const width = `${Math.round(chrome.width)}px`;
  if (doc.style.getPropertyValue('--fp-chrome-left') !== left) {
    doc.style.setProperty('--fp-chrome-left', left);
  }
  if (doc.style.getPropertyValue('--fp-chrome-width') !== width) {
    doc.style.setProperty('--fp-chrome-width', width);
  }
}
